import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import swal from '@sweetalert/with-react';

import { filterArrayOfObjects, useDebouncedValue, deleteData, sortArrayOfObjects, loadSpecificData } from '../../../js/Helpers';
import { saveDataLineEditor } from '../../../js/SaveFunctions';

import SortToggle from '../../SortToggle';
import SchoolDataFilter, { applySchoolDataFilters } from '../../SchoolDataFilter';
import UploadModal from '../../UploadModal';
import EditStudentData from '../modals/edit_student';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSearch, faEdit, faPlus, faUpload, faMinusSquare, faSortDown, faCheckSquare } from '@fortawesome/free-solid-svg-icons';
import { faTrashAlt, faSquare as farSquare } from '@fortawesome/free-regular-svg-icons';

const GENDER_OPTIONS = [
	{value: 'M', display: 'Male'},
	{value: 'F', display: 'Female'},
	{value: 'O', display: 'Other'},
];

const buildStudentDraft = (student_info = {}) => ({
	student_id: student_info.student_id,
	student_code: student_info.student_code || '',
	last_name: student_info.last_name || '',
	first_name: student_info.first_name || '',
	grade: student_info.grade ? `${student_info.grade}` : '',
	gender: student_info.gender ? `${student_info.gender}` : '',
});

const mergeSavedStudentIntoList = (student_list = [], old_student_id, saved_student) => {
	const old_student_id_string = `${old_student_id}`;
	const new_student_id_string = `${saved_student.student_id}`;
	const original_student_index = student_list.findIndex((student_info) => `${student_info.student_id}` === old_student_id_string);

	if(old_student_id_string === new_student_id_string)
	{
		return student_list.map((student_info) => (
			`${student_info.student_id}` === old_student_id_string ? saved_student : student_info
		));
	}

	const next_student_list = student_list.filter((student_info) => (
		`${student_info.student_id}` !== old_student_id_string && `${student_info.student_id}` !== new_student_id_string
	));

	if(original_student_index === -1) return [...next_student_list, saved_student];

	const safe_insert_index = Math.min(original_student_index, next_student_list.length);
	return [
		...next_student_list.slice(0, safe_insert_index),
		saved_student,
		...next_student_list.slice(safe_insert_index),
	];
};

const removeStudentsFromList = (student_list = [], student_ids = new Set()) => (
	(Array.isArray(student_list) ? student_list : []).filter((student_info) => !student_ids.has(`${student_info.student_id}`))
);

const collectStudentsForRestore = (student_list = [], student_ids = new Set()) => (
	(Array.isArray(student_list) ? student_list : []).reduce((results, student_info, index) => {
		if(student_ids.has(`${student_info.student_id}`)) results.push({student_info, index});
		return results;
	}, [])
);

const restoreStudentsIntoList = (student_list = [], students_to_restore = []) => {
	const safe_student_list = Array.isArray(student_list) ? student_list : [];
	const restore_id_set = new Set(students_to_restore.map(({student_info}) => `${student_info.student_id}`));
	const next_student_list = safe_student_list.filter((student_info) => !restore_id_set.has(`${student_info.student_id}`));

	students_to_restore
		.slice()
		.sort((student_a, student_b) => student_a.index - student_b.index)
		.forEach(({student_info, index}) => {
			const safe_insert_index = Math.min(index, next_student_list.length);
			next_student_list.splice(safe_insert_index, 0, student_info);
		});

	return next_student_list;
};

const studentsMatch = (student_a = {}, student_b = {}) => {
	const normalized_a = buildStudentDraft(student_a);
	const normalized_b = buildStudentDraft(student_b);

	return normalized_a.student_code.trim() === normalized_b.student_code.trim()
		&& normalized_a.last_name.trim() === normalized_b.last_name.trim()
		&& normalized_a.first_name.trim() === normalized_b.first_name.trim()
		&& `${normalized_a.grade}` === `${normalized_b.grade}`
		&& `${normalized_a.gender}` === `${normalized_b.gender}`;
};

export default function DatabaseDataStudents({ schoolInfo, databaseData, setDatabaseData, scheduleStructureData, scheduleInfo, courses, teachers, departments, sections, labels, dataIsLoading, handlePullNewData, readOnlyUpload = false }) {
	const [isLoading, setIsLoading] = useState(true);
	const [handleChangeComplete, setHandleChangeComplete] = useState(false);

	const [filteredResults, setFilteredResults] = useState([]);
	const [filterActive, setFilterActive] = useState(false);
	const [displayResults, setDisplayResults] = useState([]);
	const [isSearching, setIsSearching] = useState(false);
	const [searchValue, setSearchValue] = useState('');
	const debouncedSearchValue = useDebouncedValue(searchValue, 120);
	
	const [showEditDataModal, setShowEditDataModal] = useState(false);
	const [showUploadModal, setShowUploadModal] = useState(false);
	const [currentDataID, setCurrentDataID] = useState(null);
	
	const [selectedData, setSelectedData] = useState([]);
	const [dontShowDeletePopup, setDontShowDeletePopup] = useState(false);
	const [deletingStudentIDs, setDeletingStudentIDs] = useState([]);
	const [isDeletingSelected, setIsDeletingSelected] = useState(false);

	const [sortedBy, setSortedBy] = useState('last_name');
	const [sortAscDesc, setSortAscDesc] = useState('asc');
	const [maxStudentsToShow, setMaxStudentsToShow] = useState(50);

	const [editingCell, setEditingCell] = useState(null);
	const [cellDraft, setCellDraft] = useState(null);
	const [isSavingCell, setIsSavingCell] = useState(false);

	const filteredResultsRef = useRef(filteredResults);
	const activeEditorRef = useRef(null);
	const studentCodeInputRef = useRef(null);
	const studentLastNameInputRef = useRef(null);
	const studentFirstNameInputRef = useRef(null);
	filteredResultsRef.current = filteredResults;

	const handleIsSearching = (e) => {
		const search_value = e.target.value;

		if(search_value.length > 1)
		{
			if(e.keyCode !== 8)
			{
				setIsLoading(false);
				setIsSearching(true);
			}
			else
			{
				setIsLoading(true);
				setIsSearching(false);
			}
		}
		else
		{
			setIsLoading(false);
			setIsSearching(false);
		}
	};

	const gradeOptions = useMemo(() => [...new Set(
		databaseData.reduce((results, student_info) => {
			const grade = student_info?.grade ? `${student_info.grade}` : '';
			if(grade) results.push(grade);
			return results;
		}, [])
	)].sort((grade_a, grade_b) => Number(grade_a) - Number(grade_b)), [databaseData]);
	const gradeFilterOptions = useMemo(() => gradeOptions.map((grade) => ({grade, display: `Grade ${grade}`})), [gradeOptions]);
	const schoolDataFilterCategories = useMemo(() => ([
		{
			key: 'grade',
			title: 'Grade',
			dataKey: 'grade',
			options: gradeFilterOptions.map(grade_info => ({
				value: grade_info.grade,
				display: grade_info.display,
			})),
		},
	]), [gradeFilterOptions]);

	const getGenderDisplay = (gender_value) => {
		const gender_option = GENDER_OPTIONS.find((option) => option.value === `${gender_value}`);
		return gender_option ? gender_option.display : '--';
	};

	const isEditingField = useCallback((student_id, field) => (
		editingCell
		&& `${editingCell.student_id}` === `${student_id}`
		&& editingCell.field === field
	), [editingCell]);

	const cancelCellEdit = useCallback(() => {
		setEditingCell(null);
		setCellDraft(null);
		setIsSavingCell(false);
	}, []);

	const getStudentByID = useCallback((student_id) => (
		databaseData.find((student_info) => `${student_info.student_id}` === `${student_id}`) || null
	), [databaseData]);

	const handleSetDatabaseData = useCallback((next_database_data) => {
		if(typeof next_database_data === 'function')
		{
			setDatabaseData((current_database_data) => {
				const resolved_database_data = next_database_data(current_database_data);
				const safe_database_data = Array.isArray(resolved_database_data) ? resolved_database_data : [];

				filteredResultsRef.current = safe_database_data;
				setFilteredResults([...safe_database_data]);
				setHandleChangeComplete(true);

				return safe_database_data;
			});

			return;
		}

		const safe_database_data = Array.isArray(next_database_data) ? next_database_data : [];

		filteredResultsRef.current = safe_database_data;
		setDatabaseData(safe_database_data);
		setFilteredResults([...safe_database_data]);
		setHandleChangeComplete(true);
	}, [setDatabaseData]);

	const applyCurrentSort = useCallback((student_list = []) => {
		const sort_type = (sortedBy === 'grade') ? 'number' : null;
		return sortArrayOfObjects(Array.isArray(student_list) ? student_list : [], sortedBy, sort_type, sortAscDesc);
	}, [sortedBy, sortAscDesc]);

	const startCellEdit = useCallback((student_info, field) => {
		if(!student_info) return;

		setEditingCell({student_id: student_info.student_id, field});
		setCellDraft(buildStudentDraft(student_info));
		setIsSavingCell(false);
	}, []);

	const handleChange = useCallback(() => {
		if(filteredResultsRef.current.length === 0 && dataIsLoading) return;

		const search_bar = document.getElementsByClassName("school-data-search-bar")[0];
		const search_value = search_bar ? search_bar.value : '';
		let next_display_results = [];
		if(search_value.length > 1)
		{
			next_display_results = filterArrayOfObjects(filteredResultsRef.current, search_value, ['last_name', 'first_name', 'student_code']);
		}
		else
		{
			next_display_results = filteredResultsRef.current;
		}
		next_display_results = applyCurrentSort(next_display_results);

		setDisplayResults([...next_display_results]);
		setIsLoading(false);
		setIsSearching(false);
		setHandleChangeComplete(true);
	}, [applyCurrentSort, dataIsLoading]);

	useEffect(() => {
		setMaxStudentsToShow(50);
	}, [debouncedSearchValue]);

	useEffect(() => {
		handleChange();
	}, [debouncedSearchValue, handleChange]);

	useEffect(() => {
		if(!editingCell) return;

		const input_ref_map = {
			student_code: studentCodeInputRef,
			last_name: studentLastNameInputRef,
			first_name: studentFirstNameInputRef,
		};
		const input_ref = input_ref_map[editingCell.field];

		if(input_ref?.current)
		{
			input_ref.current.focus();
			if(typeof input_ref.current.select === 'function') input_ref.current.select();
		}
	}, [editingCell]);

	const updateDraftField = (field, value) => {
		setCellDraft((current_draft) => ({...current_draft, [field]: value}));
	};

	const handleSaveCell = useCallback(async (student_info, draft_override = null) => {
		const draft_to_save = draft_override || cellDraft;
		if(!draft_to_save || !editingCell || isSavingCell) return;

		const trimmed_student_code = draft_to_save.student_code ? draft_to_save.student_code.trim() : '';
		const trimmed_last_name = draft_to_save.last_name ? draft_to_save.last_name.trim() : '';
		const trimmed_first_name = draft_to_save.first_name ? draft_to_save.first_name.trim() : '';
		const next_grade = draft_to_save.grade ? `${draft_to_save.grade}` : '';
		const next_gender = draft_to_save.gender ? `${draft_to_save.gender}` : '';
		const draft_to_compare = {
			...student_info,
			student_code: trimmed_student_code,
			last_name: trimmed_last_name,
			first_name: trimmed_first_name,
			grade: next_grade,
			gender: next_gender,
		};

		if(studentsMatch(student_info, draft_to_compare))
		{
			cancelCellEdit();
			return;
		}

		if(!trimmed_student_code || !trimmed_last_name || !trimmed_first_name || !next_grade || !next_gender)
		{
			swal({
				title: "Missing Data",
				icon:"warning",
				buttons: {
					cancel: {
						text: "Okay",
						value: false,
						visible: true,
						className: 'blue-btn'
					}
				},
				content: (
					<div className='text-align-left'>
						<p>You are missing data! Make sure you have the following data before saving:</p>
						<ul>
							<li>Student Code</li>
							<li>First Name</li>
							<li>Last Name</li>
							<li>Gender</li>
							<li>Grade</li>
						</ul>
					</div>
				)
			});
			return;
		}

		setIsSavingCell(true);
		if(draft_override) setCellDraft(draft_override);

		const original_student_id = student_info.student_id;
		const response_data = await saveDataLineEditor({
			school_id: schoolInfo.school_id,
			schedule_version_id: schoolInfo.current_schedule_version_id,
			data_type: 'student',
			data_to_save: {
				student_id: original_student_id,
				student_code: trimmed_student_code,
				last_name: trimmed_last_name,
				first_name: trimmed_first_name,
				grade: next_grade,
				gender: next_gender,
			}
		});

		if(!response_data?.new_data_id)
		{
			setIsSavingCell(false);
			swal({
				title: "Unable to Save",
				icon:"warning",
				buttons: {
					cancel: {
						text: "Okay",
						value: false,
						visible: true,
						className: 'blue-btn'
					}
				},
				content: (
					<div>
						<p>We couldn&rsquo;t save this student value. Please try again.</p>
					</div>
				)
			});
			return;
		}

		const saved_student_id = response_data.new_data_id;
		let saved_student = {
			...student_info,
			student_id: saved_student_id,
			student_code: trimmed_student_code,
			last_name: trimmed_last_name,
			first_name: trimmed_first_name,
			grade: next_grade,
			gender: next_gender,
		};

		const refreshed_student = await loadSpecificData({
			data_type:'student',
			data_id:saved_student_id,
			data_version_id:schoolInfo.current_data_version_id,
			schedule_version_id:schoolInfo.current_schedule_version_id,
			school_id:schoolInfo.school_id,
		});

		if(refreshed_student && !refreshed_student.msg)
		{
			if(`${refreshed_student.student_code || ''}` !== trimmed_student_code)
			{
				setIsSavingCell(false);
				swal({
					title: "Unable to Verify Save",
					icon:"warning",
					buttons: {cancel: {text: "Okay", value: false, visible: true, className: 'blue-btn'}},
					content: (<div><p>The saved student code did not match the casing you entered. Please try again.</p></div>)
				});
				return;
			}
			saved_student = refreshed_student;
		}

		const next_database_data = mergeSavedStudentIntoList(databaseData, original_student_id, saved_student);
		const next_filtered_results = mergeSavedStudentIntoList(filteredResults, original_student_id, saved_student);

		setDatabaseData(next_database_data);
		setFilteredResults(next_filtered_results);
		setSelectedData((current_selected_data) => {
			if(`${saved_student_id}` === `${original_student_id}`) return current_selected_data;

			const next_selected_data = current_selected_data.map((selected_id) => (
				`${selected_id}` === `${original_student_id}` ? saved_student_id : selected_id
			));

			return [...new Set(next_selected_data)];
		});
		cancelCellEdit();
	}, [cellDraft, editingCell, isSavingCell, schoolInfo, databaseData, filteredResults, setDatabaseData, cancelCellEdit]);

	const handleCellBlur = (student_info, field) => {
		if(!isEditingField(student_info.student_id, field) || isSavingCell) return;

		window.setTimeout(() => {
			const active_element = document.activeElement;
			const focus_stayed_inside = activeEditorRef.current && active_element ? activeEditorRef.current.contains(active_element) : false;
			if(focus_stayed_inside) return;
			handleSaveCell(student_info);
		}, 0);
	};

	const handleCellKeyDown = async (event, student_info) => {
		if(event.key === 'Enter')
		{
			event.preventDefault();
			await handleSaveCell(student_info);
		}
		else if(event.key === 'Escape')
		{
			event.preventDefault();
			cancelCellEdit();
		}
	};

	const handleSingleSelect = (student_info, field, value) => {
		const next_draft = {...(cellDraft || buildStudentDraft(student_info)), [field]: `${value}`};
		handleSaveCell(student_info, next_draft);
	};

	useEffect(() => {
		if(!editingCell || !['grade', 'gender'].includes(editingCell.field)) return;

		const handleOutsideClick = (event) => {
			if(activeEditorRef.current && activeEditorRef.current.contains(event.target)) return;

			const student_info = getStudentByID(editingCell.student_id);
			if(!student_info)
			{
				cancelCellEdit();
				return;
			}

			handleSaveCell(student_info);
		};

		document.addEventListener('mousedown', handleOutsideClick);
		return () => document.removeEventListener('mousedown', handleOutsideClick);
	}, [editingCell, handleSaveCell, getStudentByID, cancelCellEdit]);

	const showMoreStudents = () => {
		setMaxStudentsToShow((current_max_students) => current_max_students + 50);
	};

	const handleSortClick = (sortedData, sorted_by, sort_asc_desc) => {
		cancelCellEdit();
		setSortedBy(sorted_by);
		setSortAscDesc(sort_asc_desc);
		setDisplayResults([...sortedData]);
	};

	const handleGradeFilterClick = (selectedFilters = {}) => {
		cancelCellEdit();
		setFilterActive(Object.keys(selectedFilters).some(filter_key => (selectedFilters[filter_key] || []).length > 0));
		const next_filtered_results = applySchoolDataFilters(databaseData, selectedFilters, schoolDataFilterCategories);
		setFilteredResults([...next_filtered_results]);
		setSelectedData([]);
		setMaxStudentsToShow(50);
	};

	const toggleEditData = (dataID) => {
		cancelCellEdit();
		if(showEditDataModal)
		{
			setShowEditDataModal(false);
			return;
		}

		setCurrentDataID(dataID);
		setShowEditDataModal(true);
	};

	const toggleDontShow = () => {
		setDontShowDeletePopup(!dontShowDeletePopup);
	};

	const toggleSelectData = (data_id) => {
		if(isDeletingSelected) return;

		const data_selected_index = selectedData.findIndex((data) => `${data}` === `${data_id}`);

		if(data_selected_index !== -1)
		{
			selectedData.splice(data_selected_index, 1);
		}
		else
		{
			selectedData.push(data_id);
		}

		setSelectedData([...selectedData]);
	};

	const toggleSelectAll = () => {
		if(isDeletingSelected) return;

		const any_data_selected = selectedData.length > 0;
		
		let new_selected_data = [];

		if(!any_data_selected) 
		{
			new_selected_data = displayResults.reduce((results, student_info) => {
				results.push(student_info.student_id);
				return results;
			}, []);
		}

		setSelectedData(new_selected_data);
	};

	const handeDeleteData = async (data_ids) => {
		if(isDeletingSelected || !Array.isArray(data_ids) || data_ids.length <= 0) return;

		const normalized_data_ids = data_ids.map((data_id) => `${data_id}`);
		const data = {school_id:schoolInfo.school_id, schedule_version_id:schoolInfo.current_schedule_version_id, data_type:'student', data_ids:normalized_data_ids};
		let delete_data_bool = true;
		const delete_count = normalized_data_ids.length;
		const student_label = delete_count === 1 ? 'student' : 'students';
		
		if(!dontShowDeletePopup)
		{
			const options =  {
				title: "Are you sure?",
				icon: "warning",
				dangerMode: true,
				buttons: {
					cancel: {
						text: "Cancel",
						value: false,
						visible: true,
						className: 'gray-btn'
					},
					confirm: {
						text: `Yes, delete ${delete_count} ${student_label}`,
						value: true,
						visible: true,
						className: 'red-btn'
					},
				},
				content: (
					<div>
						<div>
							<p>Do you really want to delete the following students:</p>
							<div style={{marginLeft:'50px', textAlign:'left'}}>
								{normalized_data_ids.map((data_id, i) => {
									const data_index = databaseData.findIndex((student_info) => `${student_info.student_id}` === `${data_id}`);
									const data_info = databaseData[data_index];

									return (
										<div key={i}>{data_info.first_name} {data_info.last_name}</div>
									);
								})}
							</div>
							<p className='red-text'>This will <strong>permanently</strong> remove them from the database.</p>
						</div>
						<div className='sweet-alert-dont-show-message' onClick={() => toggleDontShow('remove')}>
							<label className="checkmark_container">Please don&rsquo;t show me this message again
								<input type="checkbox" />
								<span className="checkmark"></span>
							</label>
						</div>
					</div>
				)
			};
		
			delete_data_bool = await swal(options);
		}
		
		if(delete_data_bool)
		{
			cancelCellEdit();
			const deleting_ids = normalized_data_ids;
			const deleting_id_set = new Set(deleting_ids);
			const removed_database_students = collectStudentsForRestore(databaseData, deleting_id_set);
			const removed_filtered_students = collectStudentsForRestore(filteredResults, deleting_id_set);
			const removed_display_students = collectStudentsForRestore(displayResults, deleting_id_set);

			setDeletingStudentIDs(deleting_ids);
			setIsDeletingSelected(true);

			try
			{
				const delete_request = deleteData(data, {throwOnError:true});

				setDatabaseData((current_database_data) => {
					const next_database_data = removeStudentsFromList(current_database_data, deleting_id_set);
					return next_database_data;
				});
				setFilteredResults((current_filtered_results) => {
					const next_filtered_results = removeStudentsFromList(current_filtered_results, deleting_id_set);
					filteredResultsRef.current = next_filtered_results;
					return next_filtered_results;
				});
				setDisplayResults((current_display_results) => {
					const next_display_results = removeStudentsFromList(current_display_results, deleting_id_set);
					return next_display_results;
				});
				setSelectedData((current_selected_data) => current_selected_data.filter((student_id) => !deleting_id_set.has(`${student_id}`)));

				const delete_response = await delete_request;
				const not_deleted_ids = Array.isArray(delete_response?.not_deleted_ids)
					? delete_response.not_deleted_ids.map((student_id) => `${student_id}`)
					: [];

				if(not_deleted_ids.length > 0)
				{
					const not_deleted_id_set = new Set(not_deleted_ids);
					const database_students_to_restore = removed_database_students.filter(({student_info}) => not_deleted_id_set.has(`${student_info.student_id}`));
					const filtered_students_to_restore = removed_filtered_students.filter(({student_info}) => not_deleted_id_set.has(`${student_info.student_id}`));
					const display_students_to_restore = removed_display_students.filter(({student_info}) => not_deleted_id_set.has(`${student_info.student_id}`));

					setDatabaseData((current_database_data) => {
						const next_database_data = restoreStudentsIntoList(current_database_data, database_students_to_restore);
						return next_database_data;
					});
					setFilteredResults((current_filtered_results) => {
						const next_filtered_results = restoreStudentsIntoList(current_filtered_results, filtered_students_to_restore);
						filteredResultsRef.current = next_filtered_results;
						return next_filtered_results;
					});
					setDisplayResults((current_display_results) => {
						const next_display_results = restoreStudentsIntoList(current_display_results, display_students_to_restore);
						return next_display_results;
					});
					const not_deleted_label = not_deleted_ids.length === 1 ? 'student was' : 'students were';
					swal({
						title: "Students Were Not Deleted",
						icon:"warning",
						buttons: {
							cancel: {
								text: "Okay",
								value: false,
								visible: true,
								className: 'blue-btn'
							}
						},
						content: (
							<div>
								<p>{not_deleted_ids.length} {not_deleted_label} not deleted. Refresh and try again if needed.</p>
							</div>
						)
					});
				}
			}
			catch(e)
			{
				setDatabaseData((current_database_data) => {
					const next_database_data = restoreStudentsIntoList(current_database_data, removed_database_students);
					return next_database_data;
				});
				setFilteredResults((current_filtered_results) => {
					const next_filtered_results = restoreStudentsIntoList(current_filtered_results, removed_filtered_students);
					filteredResultsRef.current = next_filtered_results;
					return next_filtered_results;
				});
				setDisplayResults((current_display_results) => {
					const next_display_results = restoreStudentsIntoList(current_display_results, removed_display_students);
					return next_display_results;
				});
				swal({
					title: "Unable to Delete",
					icon:"warning",
					buttons: {
						cancel: {
							text: "Okay",
							value: false,
							visible: true,
							className: 'blue-btn'
						}
					},
					content: (
						<div>
							<p>We couldn&rsquo;t delete the selected {student_label}. Please try again.</p>
						</div>
					)
				});
			}
			finally
			{
				setIsDeletingSelected(false);
				setDeletingStudentIDs([]);
			}
		}
	};

	useEffect(() => {
		if(!showEditDataModal)
		{
			setCurrentDataID(null);
			const search_bar = document.getElementsByClassName("school-data-search-bar")[0];
			const search_value = search_bar ? search_bar.value : '';
			if(search_value.length > 0) handleChange();
		}
	}, [showEditDataModal, handleChange]);

	useEffect(() => {
		handleChange();
	}, [filteredResults, handleChange]);

	useEffect(() => {
		if(dataIsLoading || filterActive) return;

		filteredResultsRef.current = Array.isArray(databaseData) ? databaseData : [];
		setFilteredResults([...filteredResultsRef.current]);
		setHandleChangeComplete(false);
	}, [databaseData, dataIsLoading, filterActive]);

	const visibleResults = displayResults.slice(0, maxStudentsToShow);
	const hasMoreStudents = displayResults.length > maxStudentsToShow;
	const row_style = {gridTemplateColumns:'35px 2fr 2fr 2fr 1fr 1fr 70px'};

	return (
		<>
		{showEditDataModal &&
			<EditStudentData schoolInfo={schoolInfo} dataID={currentDataID} toggleEditData={toggleEditData} scheduleStructureData={scheduleStructureData} scheduleInfo={scheduleInfo} students={databaseData} courses={courses} teachers={teachers} departments={departments} sections={sections} labels={labels} setDatabaseData={handleSetDatabaseData}/>
		}
		{showUploadModal && !readOnlyUpload &&
			<UploadModal schoolInfo={schoolInfo} scheduleInfo={scheduleInfo} uploadTitle='Students' uploadAPIName='studentdata' closeModal={() => setShowUploadModal(false)} handlePullNewData={handlePullNewData}/>
		}
		<div className='school-data-content-screen'>
			<h1 className='school-data-main-heading capitalize'>Students</h1>
			<div className='school-data-top-bar'>
				<div className='school-data-search-filter-row'>
					<div className='school-data-search-bar-container'>
						<input className='school-data-search-bar' placeholder="Search for students..." value={searchValue} onChange={(e) => setSearchValue(e.target.value)} onKeyDown={(e) => handleIsSearching(e)} />
						<FontAwesomeIcon className='school-data-search-bar-icon' icon={faSearch}/>
					</div>
					<SchoolDataFilter categories={schoolDataFilterCategories} onChange={handleGradeFilterClick} resultCount={displayResults.length}/>
				</div>
				{!readOnlyUpload && <div className='btn btn-icon-left upload-action-btn' onClick={() => {
					cancelCellEdit();
					setShowUploadModal(true);
				}}>
					<FontAwesomeIcon icon={faUpload} />
					<div>Upload</div>
				</div>}
			</div>
			<div className='school-data-database-display'>
				<div className='school-data-database-row school-data-database-display-header fixed-heading-on-scroll' style={row_style}>
					<div></div>
					<div className='school-data-database-header-col'>Code <SortToggle handleOnClick={handleSortClick} sortKey='student_code' sortType='text' passedData={displayResults}/></div>
					<div className='school-data-database-header-col'>Last Name <SortToggle handleOnClick={handleSortClick} sortKey='last_name' sortType='text' passedData={displayResults}/></div>
					<div className='school-data-database-header-col'>First Name <SortToggle handleOnClick={handleSortClick} sortKey='first_name' sortType='text' passedData={displayResults}/></div>
					<div className='school-data-database-header-col'>Grade <SortToggle handleOnClick={handleSortClick} sortKey='grade' sortType='number' passedData={displayResults}/></div>
					<div className='school-data-database-header-col'>Gender <SortToggle handleOnClick={handleSortClick} sortKey='gender' sortType='text' passedData={displayResults}/></div>
					<div className='school-data-database-add-new-container' onClick={() => toggleEditData(null)}>
						<div className='school-data-database-add-new-btn'>
							<FontAwesomeIcon className='white-text' icon={faPlus}/>
						</div>
					</div>
				</div>
				{displayResults.length > 0 &&
					<div className='select-all-row'>
						<div className={`select-all-container ${selectedData.length > 0 && 'blue-text'}`} onClick={toggleSelectAll}>
							<FontAwesomeIcon icon={selectedData.length > 0 ? faMinusSquare : farSquare}/>
							<FontAwesomeIcon className='select-all-arrow' icon={faSortDown}/>
						</div>
						{selectedData.length > 0 &&
							<div className='small-text'>
								<div className='tooltip' data-tooltip-text={isDeletingSelected ? 'Deleting students' : 'Delete students'} onClick={!isDeletingSelected ? () => handeDeleteData(selectedData) : null}>
									<div className={isDeletingSelected ? 'dark-gray-text' : 'red-link'}>{isDeletingSelected ? 'Deleting selected...' : `Delete ${selectedData.length} selected`}</div>
								</div>
							</div>
						}
					</div>
				}
				{(!handleChangeComplete && isLoading) ?
				(
					<div className='school-data-database-message-container'>
						<img src={require('../../../images/balls.gif')} alt='loading...' style={{height:'80px'}}/>
					</div>
				): isSearching ?
				(
					<div className='school-data-database-message-container'>
						<img src={require('../../../images/searching.gif')} alt='searching...' style={{height:'80px'}}/>
					</div>
				):
				(
					<>
					{displayResults.length === 0 &&
						<div className='school-data-database-message-container'>
							<h4>Sorry, no results were found!</h4>
						</div>
					}
					{visibleResults.map((data_info) => {
						const student_id = data_info.student_id;
						const data_selected = selectedData.some((selected_id) => `${selected_id}` === `${student_id}`);
						const student_deleting = deletingStudentIDs.includes(`${student_id}`);
						const editing_student_id = editingCell?.student_id;

						if(student_deleting)
						{
							return (
								<div className='school-data-database-row school-data-label-row-deleting' style={row_style} key={student_id}>
									<div className='school-data-label-row-full-loader' style={{gridColumn:'1 / -1'}}>
										<img src={require('../../../images/balls.gif')} alt='deleting...' style={{height:'35px'}}/>
									</div>
								</div>
							);
						}

						return (
							<div className='school-data-database-row' style={row_style} key={student_id}>
								<div className='school-data-row-checkbox-cell' onClick={() => toggleSelectData(student_id)}>
									<FontAwesomeIcon className={data_selected ? 'blue-text' : 'dark-gray-text'} icon={data_selected ? faCheckSquare : farSquare}/>
								</div>
								<div
									ref={isEditingField(student_id, 'student_code') ? activeEditorRef : null}
									className={isEditingField(student_id, 'student_code') ? 'school-data-inline-editor-container' : 'school-data-inline-editable-cell'}
									onClick={!editing_student_id ? () => startCellEdit(data_info, 'student_code') : undefined}
								>
									{isEditingField(student_id, 'student_code') ?
										(isSavingCell ?
											<div className='school-data-inline-saving-cell'>
												<img src={require('../../../images/balls.gif')} alt='saving...' className='school-data-inline-saving-gif'/>
											</div>
										:
											<input
												ref={studentCodeInputRef}
												className='school-data-inline-edit-input'
												value={cellDraft?.student_code || ''}
												disabled={isSavingCell}
												onChange={(e) => updateDraftField('student_code', e.target.value)}
												onKeyDown={(e) => handleCellKeyDown(e, data_info)}
												onBlur={() => handleCellBlur(data_info, 'student_code')}
											/>
										)
									:
										<div className='medium-text'>{data_info.student_code ? data_info.student_code : '--'}</div>
									}
								</div>
								<div
									ref={isEditingField(student_id, 'last_name') ? activeEditorRef : null}
									className={isEditingField(student_id, 'last_name') ? 'school-data-inline-editor-container' : 'school-data-inline-editable-cell'}
									onClick={!editing_student_id ? () => startCellEdit(data_info, 'last_name') : undefined}
								>
									{isEditingField(student_id, 'last_name') ?
										(isSavingCell ?
											<div className='school-data-inline-saving-cell'>
												<img src={require('../../../images/balls.gif')} alt='saving...' className='school-data-inline-saving-gif'/>
											</div>
										:
											<input
												ref={studentLastNameInputRef}
												className='school-data-inline-edit-input'
												value={cellDraft?.last_name || ''}
												disabled={isSavingCell}
												onChange={(e) => updateDraftField('last_name', e.target.value)}
												onKeyDown={(e) => handleCellKeyDown(e, data_info)}
												onBlur={() => handleCellBlur(data_info, 'last_name')}
											/>
										)
									:
										<div>{data_info.last_name}</div>
									}
								</div>
								<div
									ref={isEditingField(student_id, 'first_name') ? activeEditorRef : null}
									className={isEditingField(student_id, 'first_name') ? 'school-data-inline-editor-container' : 'school-data-inline-editable-cell'}
									onClick={!editing_student_id ? () => startCellEdit(data_info, 'first_name') : undefined}
								>
									{isEditingField(student_id, 'first_name') ?
										(isSavingCell ?
											<div className='school-data-inline-saving-cell'>
												<img src={require('../../../images/balls.gif')} alt='saving...' className='school-data-inline-saving-gif'/>
											</div>
										:
											<input
												ref={studentFirstNameInputRef}
												className='school-data-inline-edit-input'
												value={cellDraft?.first_name || ''}
												disabled={isSavingCell}
												onChange={(e) => updateDraftField('first_name', e.target.value)}
												onKeyDown={(e) => handleCellKeyDown(e, data_info)}
												onBlur={() => handleCellBlur(data_info, 'first_name')}
											/>
										)
									:
										<div>{data_info.first_name}</div>
									}
								</div>
								<div
									ref={isEditingField(student_id, 'grade') ? activeEditorRef : null}
									className={isEditingField(student_id, 'grade') ? 'school-data-inline-editor-container' : 'school-data-inline-editable-cell'}
									onClick={!editing_student_id ? () => startCellEdit(data_info, 'grade') : undefined}
								>
									{isEditingField(student_id, 'grade') ?
										(isSavingCell ?
											<div className='school-data-inline-saving-cell school-data-inline-saving-cell-floating'>
												<img src={require('../../../images/balls.gif')} alt='saving...' className='school-data-inline-saving-gif'/>
											</div>
										:
											<div className='school-data-inline-select-popover'>
												{gradeOptions.map((grade_value) => {
													const grade_selected = `${cellDraft?.grade || ''}` === `${grade_value}`;

													return (
														<button key={grade_value} type='button' className={`school-data-inline-select-option ${grade_selected ? 'school-data-inline-select-option-selected' : ''}`} onClick={() => handleSingleSelect(data_info, 'grade', grade_value)}>
															{grade_value}
														</button>
													);
												})}
											</div>
										)
									:
										<div className='medium-text'>{data_info.grade}</div>
									}
								</div>
								<div
									ref={isEditingField(student_id, 'gender') ? activeEditorRef : null}
									className={isEditingField(student_id, 'gender') ? 'school-data-inline-editor-container' : 'school-data-inline-editable-cell'}
									onClick={!editing_student_id ? () => startCellEdit(data_info, 'gender') : undefined}
								>
									{isEditingField(student_id, 'gender') ?
										(isSavingCell ?
											<div className='school-data-inline-saving-cell school-data-inline-saving-cell-floating'>
												<img src={require('../../../images/balls.gif')} alt='saving...' className='school-data-inline-saving-gif'/>
											</div>
										:
											<div className='school-data-inline-select-popover'>
												{GENDER_OPTIONS.map((gender_option) => {
													const gender_selected = `${cellDraft?.gender || ''}` === `${gender_option.value}`;

													return (
														<button key={gender_option.value} type='button' className={`school-data-inline-select-option ${gender_selected ? 'school-data-inline-select-option-selected' : ''}`} onClick={() => handleSingleSelect(data_info, 'gender', gender_option.value)}>
															{gender_option.display}
														</button>
													);
												})}
											</div>
										)
									:
										<div className='medium-text'>{getGenderDisplay(data_info.gender)}</div>
									}
								</div>
								<div className='school-data-database-row-btn-container'>
									<FontAwesomeIcon className={isDeletingSelected ? 'dark-gray-text' : 'gray-to-dark-blue-link'} icon={faEdit} onClick={!isDeletingSelected ? () => toggleEditData(student_id) : null}/>
									<FontAwesomeIcon className={isDeletingSelected ? 'dark-gray-text' : 'gray-to-red-link'} icon={faTrashAlt} onClick={!isDeletingSelected ? () => handeDeleteData([student_id]) : null}/>
								</div>
							</div>
						);
					})}
					</>
				)}
			</div>
			{hasMoreStudents &&
				<div className='btn blue-btn load-more-btn' onClick={showMoreStudents}>Load More Students</div>
			}
		</div>
		</>
	);
}
